import { CARD_BACKGROUND_COUNT } from "./cardBackgrounds.js";
import { COLORWAY_COUNT } from "./colorway.js";

export type Finish = "matte" | "silver" | "gold" | "bronze" | "holographic";

export const FINISHES: readonly Finish[] = [
  "matte",
  "silver",
  "gold",
  "bronze",
  "holographic",
];

export const PATTERN_VARIANTS = 8;

// Cosmetics-as-code. Every axis that affects the rendered card is
// encoded into the short string. Sliders in the tuner write straight
// back through encode(); a new code recovers the exact same spec via
// decode(). No global tunables, no parallel knob path.
export interface SkinSpec {
  pattern: {
    index: number;        // 0..7
    offsetX: number;      // 0..1
    offsetY: number;      // 0..1
    scale: number;        // 0.5..3
    overlayAlpha: number; // 0..1
  };
  /** Index into SkinAssets.cardBackgrounds. The dominant card body colour. */
  cardBackground: number;
  /** Index into SkinAssets.colorways. Picks the palette for accent regions. */
  colorway: number;
  tint: {
    hue: number;        // -1..1 (multiplied by 180 to feed ColorMatrixFilter)
    saturation: number; // 0..2
    brightness: number; // 0..2
  };
  finish: Finish;
  /** Foil intensity for silver / gold / bronze / holographic. Ignored for matte. */
  finishStrength: number; // 0..1
  /** Foil shader pixel cell size. Lower = finer / less chunky. */
  cellSize: number;       // 1..8
  /** Widens the foil gloss gate so sparse finishMask patterns still read as foil. */
  coverageBias: number;   // 0..1
  /** Pattern-relief depth. 0 = flat ink, 1.5 = full PBR-ish. */
  depth: number;          // 0..1.5
  /** Surface condition. 0 = factory-new, 1 = battle-scarred. */
  wear: number;           // 0..1
}

// ── Code packing ──────────────────────────────────────────────────────
//
// Each axis is quantised to a fixed number of bits and packed in order
// (MSB-first across the wire). The base32 alphabet below is RFC 4648-hex
// lowercase: stable, URL-safe, no lookalike ambiguity for the digits
// we're using.

const ALPHABET = "0123456789abcdefghijklmnopqrstuv";

interface FieldSchema {
  readonly bits: number;
  readonly min: number;
  readonly max: number;
}

// Order is the wire format. Adding a field is a breaking change to the
// code shape — bump CODE_LENGTH accordingly. Removing one is also a
// break; the package is still a spike so we don't carry a migration.
const SCHEMA = {
  patternIndex:    { bits: 3, min: 0, max: PATTERN_VARIANTS - 1 },
  patternOffsetX:  { bits: 6, min: 0, max: 1 },
  patternOffsetY:  { bits: 6, min: 0, max: 1 },
  patternScale:    { bits: 6, min: 0.5, max: 3 },
  overlayAlpha:    { bits: 6, min: 0, max: 1 },
  cardBackground:  { bits: 3, min: 0, max: CARD_BACKGROUND_COUNT - 1 },
  colorway:        { bits: 3, min: 0, max: COLORWAY_COUNT - 1 },
  hue:             { bits: 6, min: -1, max: 1 },
  saturation:      { bits: 6, min: 0, max: 2 },
  brightness:      { bits: 6, min: 0, max: 2 },
  finish:          { bits: 3, min: 0, max: FINISHES.length - 1 },
  finishStrength:  { bits: 6, min: 0, max: 1 },
  cellSize:        { bits: 4, min: 1, max: 8 },
  coverageBias:    { bits: 6, min: 0, max: 1 },
  depth:           { bits: 6, min: 0, max: 1.5 },
  wear:            { bits: 6, min: 0, max: 1 },
} satisfies Record<string, FieldSchema>;

const FIELD_ORDER: ReadonlyArray<keyof typeof SCHEMA> = [
  "patternIndex",
  "patternOffsetX",
  "patternOffsetY",
  "patternScale",
  "overlayAlpha",
  "cardBackground",
  "colorway",
  "hue",
  "saturation",
  "brightness",
  "finish",
  "finishStrength",
  "cellSize",
  "coverageBias",
  "depth",
  "wear",
];

const TOTAL_BITS = FIELD_ORDER.reduce((sum, key) => sum + SCHEMA[key].bits, 0);
const PADDED_BITS = Math.ceil(TOTAL_BITS / 5) * 5;
export const CODE_LENGTH = PADDED_BITS / 5;

class BitWriter {
  private bits = "";
  push(value: number, width: number): void {
    const clamped = Math.max(0, Math.min((1 << width) - 1, Math.round(value)));
    this.bits += clamped.toString(2).padStart(width, "0");
  }
  toCode(): string {
    const padded = this.bits.padEnd(PADDED_BITS, "0");
    let out = "";
    for (let i = 0; i < padded.length; i += 5) {
      const chunk = padded.slice(i, i + 5);
      const idx = Number.parseInt(chunk, 2);
      out += ALPHABET[idx];
    }
    return out;
  }
}

class BitReader {
  private bits = "";
  private pos = 0;
  constructor(code: string) {
    for (const ch of code) {
      const idx = ALPHABET.indexOf(ch);
      if (idx < 0) throw new Error(`decode: invalid char "${ch}"`);
      this.bits += idx.toString(2).padStart(5, "0");
    }
  }
  pull(width: number): number {
    const slice = this.bits.slice(this.pos, this.pos + width);
    this.pos += width;
    return Number.parseInt(slice, 2);
  }
}

function quantise(value: number, schema: FieldSchema): number {
  const maxQ = (1 << schema.bits) - 1;
  const t = (value - schema.min) / (schema.max - schema.min);
  return Math.max(0, Math.min(maxQ, Math.round(t * maxQ)));
}

function dequantise(raw: number, schema: FieldSchema): number {
  const maxQ = (1 << schema.bits) - 1;
  return schema.min + (raw / maxQ) * (schema.max - schema.min);
}

export function encode(spec: SkinSpec): string {
  const w = new BitWriter();
  // Integer fields push raw — the bit width is sized for the max value
  // directly, no continuous-range rescale.
  w.push(spec.pattern.index,                                          SCHEMA.patternIndex.bits);
  w.push(quantise(spec.pattern.offsetX,      SCHEMA.patternOffsetX),  SCHEMA.patternOffsetX.bits);
  w.push(quantise(spec.pattern.offsetY,      SCHEMA.patternOffsetY),  SCHEMA.patternOffsetY.bits);
  w.push(quantise(spec.pattern.scale,        SCHEMA.patternScale),    SCHEMA.patternScale.bits);
  w.push(quantise(spec.pattern.overlayAlpha, SCHEMA.overlayAlpha),    SCHEMA.overlayAlpha.bits);
  w.push(spec.cardBackground,                                         SCHEMA.cardBackground.bits);
  w.push(spec.colorway,                                               SCHEMA.colorway.bits);
  w.push(quantise(spec.tint.hue,             SCHEMA.hue),             SCHEMA.hue.bits);
  w.push(quantise(spec.tint.saturation,      SCHEMA.saturation),      SCHEMA.saturation.bits);
  w.push(quantise(spec.tint.brightness,      SCHEMA.brightness),      SCHEMA.brightness.bits);
  w.push(FINISHES.indexOf(spec.finish),                               SCHEMA.finish.bits);
  w.push(quantise(spec.finishStrength,       SCHEMA.finishStrength),  SCHEMA.finishStrength.bits);
  w.push(quantise(spec.cellSize,             SCHEMA.cellSize),        SCHEMA.cellSize.bits);
  w.push(quantise(spec.coverageBias,         SCHEMA.coverageBias),    SCHEMA.coverageBias.bits);
  w.push(quantise(spec.depth,                SCHEMA.depth),           SCHEMA.depth.bits);
  w.push(quantise(spec.wear,                 SCHEMA.wear),            SCHEMA.wear.bits);
  return w.toCode();
}

export function decode(code: string): SkinSpec {
  const r = new BitReader(code);
  const patternIndex   = r.pull(SCHEMA.patternIndex.bits) % PATTERN_VARIANTS;
  const patternOffsetX = dequantise(r.pull(SCHEMA.patternOffsetX.bits), SCHEMA.patternOffsetX);
  const patternOffsetY = dequantise(r.pull(SCHEMA.patternOffsetY.bits), SCHEMA.patternOffsetY);
  const patternScale   = dequantise(r.pull(SCHEMA.patternScale.bits),   SCHEMA.patternScale);
  const overlayAlpha   = dequantise(r.pull(SCHEMA.overlayAlpha.bits),   SCHEMA.overlayAlpha);
  const cardBackground = r.pull(SCHEMA.cardBackground.bits) % CARD_BACKGROUND_COUNT;
  const colorway       = r.pull(SCHEMA.colorway.bits) % COLORWAY_COUNT;
  const hue            = dequantise(r.pull(SCHEMA.hue.bits),            SCHEMA.hue);
  const saturation     = dequantise(r.pull(SCHEMA.saturation.bits),     SCHEMA.saturation);
  const brightness     = dequantise(r.pull(SCHEMA.brightness.bits),     SCHEMA.brightness);
  const finishIdx      = r.pull(SCHEMA.finish.bits) % FINISHES.length;
  const finish         = FINISHES[finishIdx] ?? "matte";
  const finishStrength = dequantise(r.pull(SCHEMA.finishStrength.bits), SCHEMA.finishStrength);
  const cellSize       = dequantise(r.pull(SCHEMA.cellSize.bits),       SCHEMA.cellSize);
  const coverageBias   = dequantise(r.pull(SCHEMA.coverageBias.bits),   SCHEMA.coverageBias);
  const depth          = dequantise(r.pull(SCHEMA.depth.bits),          SCHEMA.depth);
  const wear           = dequantise(r.pull(SCHEMA.wear.bits),           SCHEMA.wear);
  return {
    pattern: {
      index: patternIndex,
      offsetX: patternOffsetX,
      offsetY: patternOffsetY,
      scale: patternScale,
      overlayAlpha,
    },
    cardBackground,
    colorway,
    tint: { hue, saturation, brightness },
    finish,
    finishStrength,
    cellSize,
    coverageBias,
    depth,
    wear,
  };
}

export function isValidCode(code: string): boolean {
  if (code.length !== CODE_LENGTH) return false;
  for (const ch of code) {
    if (!ALPHABET.includes(ch)) return false;
  }
  return true;
}
